import { getAuthHeaders } from "@/services";
import { setReturnUrl, getCurrentPath } from "@/lib/navigation/navigation";
import { userProductRoutes } from "@/lib/userProductRoutes";
import { hasMarketplaceBearerToken } from "@/lib/marketplaceAuth";
import { setPendingFavorite } from "@/lib/pendingFavorite";

export function getMarketplaceLoginPath(returnTo?: string): string {
  return userProductRoutes.login(returnTo);
}

/**
 * Fast sync check — token present. Prefer MarketplaceAuthProvider for full user.
 * Avoids an extra GET /api/user on every product click when shell already authenticated.
 */
export function isMarketplaceTokenPresent(): boolean {
  return hasMarketplaceBearerToken() || Boolean(getAuthHeaders().Authorization);
}

export async function isMarketplaceUserLoggedIn(): Promise<boolean> {
  return isMarketplaceTokenPresent();
}

type EnsureLoginActions = {
  notify?: (payload: { type: "error"; message: string }) => void;
  onNeedLogin?: (loginPath: string) => void;
  /** When provided by a page inside MarketplaceAuthProvider, skip network. */
  isLoggedIn?: boolean;
  authReady?: boolean;
};

/** Returns true when the user may open a vehicle detail page. */
export async function ensureLoggedInToViewProduct(
  productId: string,
  actions: EnsureLoginActions = {},
): Promise<boolean> {
  if (actions.authReady === false) return false;

  const isLoggedIn =
    typeof actions.isLoggedIn === "boolean"
      ? actions.isLoggedIn
      : isMarketplaceTokenPresent();
  if (isLoggedIn) return true;

  const returnPath = userProductRoutes.view(productId);
  setReturnUrl(returnPath);
  actions.onNeedLogin?.(getMarketplaceLoginPath(returnPath));
  return false;
}

/**
 * Returns true when the user may call the favourite API.
 * Guests are sent to login with the original product id stored so the
 * favourite can complete after a successful sign-in.
 *
 * When `authReady` is false, returns false without redirecting.
 */
export function ensureLoggedInToFavorite(
  productId: string,
  actions: EnsureLoginActions = {},
): boolean {
  if (actions.authReady === false) return false;
  if (typeof actions.isLoggedIn === "boolean") {
    if (actions.isLoggedIn) return true;
  } else if (isMarketplaceTokenPresent()) {
    return true;
  }

  const returnPath =
    (typeof window !== "undefined" ? getCurrentPath() : "") ||
    userProductRoutes.view(productId);
  setPendingFavorite(productId, returnPath);
  if (typeof window !== "undefined") {
    setReturnUrl(returnPath);
  }

  actions.notify?.({
    type: "error",
    message: "Please log in to save favourites.",
  });
  actions.onNeedLogin?.(getMarketplaceLoginPath(returnPath));
  return false;
}
